import { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { extractInstrumentations } from "../../extractors/instrumentationsExtractor";
import { ActionBar } from "../ActionBar";
import ConfirmationModal from "../ConfirmationModal";
import Loader from "../Loader";
import InstrumentationDuplicateModal from "./InstrumentationDuplicateModal";
import InstrumentationForm from "./InstrumentationForm";

import {
  convertLabelToSpecificationKey,
  extractCode,
  findMatchingInstrumentationType,
  htmlFormat,
  isFwr30v10Device,
  parseIntlNumber,
  url,
} from "../../utils";

import {
  extractAsset,
  extractInstrumentationStatuses,
  extractInstrumentationType,
  extractInstrumentationTypes,
  isUserInGroup,
} from "../../extractors";
import { packInstrumentationSpecifications } from "../../packmans";

import {
  apiShape,
  backendShape,
  useBrowser,
  useNotifier,
  useRules,
  withApi,
  withBackend,
} from "../../context";

import { useApi, useUser } from "../../hooks";
import BackButton from "../BackButton";
import { Column, Container, Row } from "../Grid";
import { PROJECT_ENGINEER_ROLE } from "../Specifications/SpecificationConstants";

type ExtendedOrderCodeResponse = {
  extended_order_code: string;
};

type Props = {
  readonly match?: {
    params?: {
      assetId?: string;
      nodeId?: string;
    };
  };
  readonly isSubComponent?: boolean;
  readonly cancelTarget?: string;
  readonly api: apiShape;
  readonly backend: backendShape;
};

export function InstrumentationCreate({
  api,
  backend,
  match,
  isSubComponent,
  cancelTarget,
}: Props) {
  const [showActivationModal, toggleActivationModal] = useState(false);
  const [showDuplicateTagModal, toggleDuplicateTagModal] = useState(false);
  const [instrumentationDetails, setInstrumentationDetails] = useState();
  const [duplicateTagValues, setDuplicateTagValues] = useState();
  const [duplicateTags, setDuplicateTags] = useState([]);
  const [duplicateActions, setDuplicateActions] = useState();

  const apiHook = useApi();
  const intl = useIntl();
  const rules = useRules();
  const browser = useBrowser();
  const notifier = useNotifier();
  const user = useUser();

  const orderCodeLookUp =
    instrumentationDetails &&
    (instrumentationDetails as $TSFixMe).asset?.manufacturerName ===
      "Endress+Hauser" &&
    (instrumentationDetails as $TSFixMe).asset.specifications?.[
      "eh.pcps.tmp.ordercode"
    ] &&
    /^[a-zA-Z0-9]+-+[a-zA-Z0-9]+\/[a-zA-Z0-9]+/g.exec(
      (instrumentationDetails as $TSFixMe).asset.specifications[
        "eh.pcps.tmp.ordercode"
      ].value,
    );

  const orderCode = (instrumentationDetails as $TSFixMe)?.asset
    ?.specifications?.["eh.pcps.tmp.ordercode"]?.value;

  const shouldFetchEoc = !!orderCodeLookUp && !!orderCode;

  // Extended order code is required to derive if a product is FWR30 or FWR30v10
  const { data: extendedOrderCodeResponse } =
    apiHook.get.useQuery<ExtendedOrderCodeResponse>(
      "/endress/extended_order_code_lookup",
      { order_code: orderCode },
      {
        enabled: shouldFetchEoc,
        retry: false,
        meta: { useGlobalErrorHandling: false },
      },
    );

  useEffect(() => {
    if (extendedOrderCodeResponse?.extended_order_code) {
      setInstrumentationDetails((prevInstrumentationDetails) => {
        const extendedOrderCode = extractCode(
          extendedOrderCodeResponse?.extended_order_code,
        );
        const prevDetails = prevInstrumentationDetails as $TSFixMe;
        /* istanbul ignore next */
        let { type } = prevDetails?.defaultValues || {};

        // Check if it is a FWR30v10 device and set the instrumentation type as Water level
        if (
          isFwr30v10Device(prevDetails?.asset.productCode, extendedOrderCode)
        ) {
          type = prevDetails?.types.find(
            (ele: $TSFixMe) => ele.code === "water_level",
          );
        }
        return {
          ...prevDetails,
          defaultValues: { ...prevDetails.defaultValues, type },
          asset: {
            ...prevDetails.asset,
            extendedOrderCode,
          },
        };
      });
    }
  }, [extendedOrderCodeResponse]);

  const instrumentationRules =
    instrumentationDetails && (instrumentationDetails as $TSFixMe).asset
      ? rules.instrumentation(undefined, [
          (instrumentationDetails as $TSFixMe).asset,
        ])
      : undefined;

  const isUserProjectEngineer = isUserInGroup(user, PROJECT_ENGINEER_ROLE);

  // eslint-disable-next-line camelcase
  const loadProductLookupTag = async (serial_number: $TSFixMe) => {
    try {
      const lookupResponse = await api.get("/endress/product_lookup", {
        // eslint-disable-next-line camelcase
        serial_number,
        include: "tag",
      });
      return lookupResponse.tag;
    } catch {
      return undefined;
    }
  };

  const loadData = async () => {
    try {
      let assetLoaded;
      let firmwareLoaded;
      let firmwareVersion;
      let defaultType = "undefined";
      let tag;
      const tagTypes = await api.getAll("/instrumentation/types", {
        include: "tenant",
      });
      const tenantIdsResponse = await api.getAll("/tenants", { public: true });
      const publicTenantIds = tenantIdsResponse.tenants.map(
        (tenant: $TSFixMe) => tenant.id,
      );
      const tagStatuses = await api.getAll("/instrumentation/statuses", {
        tenant_id: publicTenantIds.toString(),
      });

      if (
        // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
        match.params &&
        // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
        match.params.assetId &&
        // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
        match.params.assetId !== "undefined"
      ) {
        // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
        assetLoaded = await api.get(`/assets/${match.params.assetId}`, {
          include: "product,product.manufacturer,product.tenant,specifications",
        });
        firmwareLoaded = await api.get(
          `/assets/${
            // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
            match.params.assetId
          }/softwares`,
          { software_type_id: 1 },
        );
        // eslint-disable-next-line camelcase
        tag =
          isSubComponent && assetLoaded?.manufacturer?.name === "Endress+Hauser"
            ? await loadProductLookupTag(assetLoaded.serial_number)
            : undefined;

        if (
          firmwareLoaded &&
          firmwareLoaded.softwares &&
          firmwareLoaded.softwares.length > 0
        ) {
          firmwareVersion = firmwareLoaded.softwares[0].version_number;
        }

        const product = await api.get(`/products/${assetLoaded.product.id}`, {
          include: "categories.parent",
        });
        let tagType = findMatchingInstrumentationType(
          product.categories,
          tagTypes.instrumentation_types,
        );

        if (assetLoaded.product.product_code === "UXTR01") {
          tagType = tagTypes.instrumentation_types.find(
            (t: $TSFixMe) => t.code === "water_level",
          );
        }
        defaultType = tagType ? (tagType as $TSFixMe).code : "undefined";
      }

      let defaultTagType = tagTypes.instrumentation_types.find(
        (t: $TSFixMe) => t.code === defaultType,
      );
      if (defaultTagType) {
        defaultTagType = extractInstrumentationType(defaultTagType);
      }

      const instrumentationDefaultValues = {
        status: tagStatuses.instrumentation_statuses.find(
          (s: $TSFixMe) => s.code === "undefined",
        ),
        type: defaultTagType,
        tag,
      };
      setInstrumentationDetails({
        // @ts-expect-error TS(2345) FIXME: Argument of type '{ defaultValues: { status: any; ... Remove this comment to see the full error message
        defaultValues: instrumentationDefaultValues,
        asset: assetLoaded ? extractAsset(assetLoaded) : null,
        firmwareVersion,
        statuses: extractInstrumentationStatuses(tagStatuses),
        types: extractInstrumentationTypes(tagTypes),
      });
    } catch (error) {
      notifier.showError(api.translateError(error));
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const onConfirmActivationModal = () => {
    toggleActivationModal(false);
    browser.navigateTo("/nodes");
  };

  const onCloseActivationModal = () => {
    toggleActivationModal(false);
  };

  const onSubmit = async (values: $TSFixMe, actions: $TSFixMe) => {
    try {
      let existingInstrumentations = [];
      if (!showDuplicateTagModal && isSubComponent) {
        const result = await api.getAll("/instrumentations", {
          include: "type",
          tag: values.tag,
          permission: "can_permit",
        });
        existingInstrumentations = extractInstrumentations(result);
      }
      setDuplicateTags(existingInstrumentations);
      if (existingInstrumentations && existingInstrumentations.length > 0) {
        toggleDuplicateTagModal(true);
        setDuplicateTagValues(values);
        setDuplicateActions(actions);
        actions.setSubmitting(false);
      } else {
        actions.setSubmitting(true);
        toggleDuplicateTagModal(false);
        setDuplicateTags([]);
        let packSpecificationValues = instrumentationRules?.get(
          "needsFullEmptySettings",
        )
          ? {
              ...values,
              full: parseIntlNumber(values.full).toString(),
              empty: parseIntlNumber(values.empty).toString(),
              blockDistance: parseIntlNumber(values.blockDistance).toString(),
              sensitivity: values.sensitivity.id,
            }
          : values;
        /* istanbul ignore else */
        if (values.mediumType) {
          packSpecificationValues = {
            ...packSpecificationValues,
            mediumType: values.mediumType.id,
          };
        }

        const newTag = {
          tag: values.tag,
          description: values.description,
          status: { id: values.status.id },
          type: { id: values.type.id },
          criticality: values.criticality
            ? values.criticality.id.toLowerCase()
            : /* istanbul ignore next */
              null,
          accessibility: values.accessibility
            ? values.accessibility.id.toLowerCase()
            : /* istanbul ignore next */
              null,
        };
        if (!values.type.tenantPublic) {
          // FIXME: this assigns the tenant of the type when creating a new instrumentation. We need real tenant handling instead here!
          (newTag as $TSFixMe).tenant = { id: values.type.tenantId };
        }

        const tag = await api.post("/instrumentations", newTag);
        // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
        if (match.params.nodeId) {
          // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
          await api.post(`/nodes/${match.params.nodeId}/instrumentations`, {
            instrumentations: [{ id: tag.id }],
          });
        }

        const specifications = packInstrumentationSpecifications(
          packSpecificationValues,
        );

        values.specifications?.forEach((specification: $TSFixMe) => {
          if (specification.key !== "" && specification.key?.name) {
            specifications[
              convertLabelToSpecificationKey(specification.key.name)
            ] = {
              value: specification.value,
              ui_visible: isUserProjectEngineer
                ? !!specification.uiVisible
                : true,
            };
          }
        });

        await api.patch(
          `/instrumentations/${tag.id}/specifications`,
          specifications,
        );

        if (
          // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
          match.params &&
          // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
          match.params.assetId &&
          // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
          match.params.assetId !== "undefined"
        ) {
          await api.patch(`/instrumentations/${tag.id}/assets`, {
            // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
            assets: [{ id: match.params.assetId }],
          });
          // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
          if (match.params && match.params.nodeId) {
            // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
            await api.delete(`/assets/${match.params.assetId}/nodes`, {
              // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
              nodes: [{ id: match.params.nodeId }],
            });
          }
        }

        if (instrumentationRules?.get("needsFullEmptySettings")) {
          await backend.patch(`/connected_instrumentations/${tag.id}`);
        }

        actions.setSubmitting(false);
        notifier.showSuccess(
          intl.formatMessage({
            id: "instrumentation_create.success_notification",
          }),
        );
        if (
          instrumentationRules?.get("navigateToIndexAfterInstrumentationCreate")
        ) {
          toggleActivationModal(true);
        } else {
          browser.navigateTo(`/instrumentations/${tag.id}`);
        }
      }
    } catch (error) {
      actions.setSubmitting(false);
      notifier.showError(api.translateError(error));
    }
  };

  const onCreateDuplicate = async () => {
    toggleDuplicateTagModal(false);
    await onSubmit(duplicateTagValues, duplicateActions);
  };

  const onCloseDuplicate = () => {
    toggleDuplicateTagModal(false);
  };

  const duplicateInstrumentationModal =
    duplicateTags?.length > 0 ? (
      <InstrumentationDuplicateModal
        id="duplicate-instrumentation-modal-dialog"
        onCreateDuplicate={onCreateDuplicate}
        onCloseDuplicate={onCloseDuplicate}
        duplicateTags={duplicateTags}
        intl={intl}
        // @ts-expect-error TS(2322) FIXME: Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
        assetId={match.params.assetId}
      />
    ) : null;

  const instrumentationForm = instrumentationDetails ? (
    <Row>
      <Column sm="6">
        {instrumentationRules?.get("showModalActivation") ? (
          <ConfirmationModal
            id="configured-radar-info-modal-dialog"
            show={showActivationModal}
            titleText={intl.formatMessage({
              id: instrumentationRules.get("modalActivationTitleKey"),
            })}
            buttonConfirmText={intl.formatMessage({ id: "button.ok" })}
            messageText={
              <FormattedMessage
                id={instrumentationRules.get("modalActivationTextKey")}
                // @ts-expect-error TS(2322) FIXME: Type '{ br: () => JSX.Element; p: (...chunks: any[... Remove this comment to see the full error message
                values={htmlFormat}
              />
            }
            onConfirm={onConfirmActivationModal}
            onClose={onCloseActivationModal}
            image={url(instrumentationRules.get("modalActivationImagePath"))}
            showCancel={false}
          />
        ) : null}
        <InstrumentationForm
          // @ts-expect-error TS(2322) FIXME: Type '{ id: string; intl: intlShape; instrumentati... Remove this comment to see the full error message
          id="instrumentation-Form"
          intl={intl}
          instrumentationStatuses={
            (instrumentationDetails as $TSFixMe).statuses
          }
          instrumentationTypes={(instrumentationDetails as $TSFixMe).types}
          defaultValues={(instrumentationDetails as $TSFixMe).defaultValues}
          assetFirmwareVersion={
            (instrumentationDetails as $TSFixMe).firmwareVersion
          }
          onSubmit={onSubmit}
          cancelTarget={cancelTarget}
          instrumentationRules={instrumentationRules}
          isUserProjectEngineer={isUserProjectEngineer}
        />
      </Column>
    </Row>
  ) : null;

  return isSubComponent ? (
    <div>
      <Row>
        <Column>
          <h2>
            <FormattedMessage id="instrumentation_create_new.header" />
          </h2>
        </Column>
      </Row>
      {instrumentationForm}
      {showDuplicateTagModal && duplicateTags.length > 0
        ? duplicateInstrumentationModal
        : null}
      <Row>
        <Column>
          <Loader loading={!instrumentationDetails} />
        </Column>
      </Row>
    </div>
  ) : (
    <Container>
      <Row>
        <Column>
          <BackButton />
        </Column>
      </Row>
      <Row>
        <Column>
          <ActionBar>
            <h1>
              <FormattedMessage id="instrumentation_create.header" />
            </h1>
          </ActionBar>
        </Column>
      </Row>
      {instrumentationForm}
      <Row>
        <Column>
          <Loader loading={!instrumentationDetails} />
        </Column>
      </Row>
    </Container>
  );
}

export default withApi(withBackend(InstrumentationCreate));
